import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import MemberService from '@/services/UsersServices/MemberService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    errors: null,
    successOnCreate: '',
    loading: false,
    roleListOptions: [],
};

export const viewMember = createAsyncThunk(
    'member/viewMember',
    async (memberId, { rejectWithValue }) => {
        try {
            const response = await MemberService.viewMember(memberId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const createMember = createAsyncThunk(
    'member/createMember',
    async (data, { rejectWithValue }) => {
        try {
            const { id: memberId } = data;
            let response;
            if (memberId) {
                response = await MemberService.updateMember(data, memberId);
            } else {
                response = await MemberService.createMember(data);
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getRoleListOptions = createAsyncThunk(
    'member/getRoleListOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await MemberService.getRoleOptionList();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const createMemberSlice = createSlice({
    name: 'createMember',
    initialState: model,
    reducers: {
        resetMemberStateData: (state) => {
            state.data = null;
            state.errors = null;
        },
        resetMemberStateOnError: (state) => {
            state.errors = null;
        },
        resetMemberStateOnSuccess: (state) => {
            state.successOnCreate = '';
        },
    },
    extraReducers: (builder) => {
        // view member
        builder.addCase(viewMember.pending, (state) => {
            state.loading = true;
        }).addCase(viewMember.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewMember.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // create member
        builder.addCase(createMember.pending, (state) => {
            state.submitting = true;
        }).addCase(createMember.fulfilled, (state, action) => {
            state.submitting = false;
            const memberId = action.meta.arg.id;
            state.successOnCreate = memberId ? 'Member updated successfully!' : 'Member created successfully!';
        }).addCase(createMember.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get role list
        builder.addCase(getRoleListOptions.pending, (state) => {
            state.loading = true;
        }).addCase(getRoleListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.roleListOptions = action.payload;
        }).addCase(getRoleListOptions.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetMemberStateData,
    resetMemberStateOnError,
    resetMemberStateOnSuccess,
} = createMemberSlice.actions;

export default createMemberSlice.reducer;
